import { getRoot, Instance, types } from "mobx-state-tree";
import { RootInstance } from "./main";
import {
  BlingsPlayer,
  expand,
  LayerType,
  modTypes,
  type IVideoPart,
} from "@blings/blings-player";
import { inspectorAPI, motionAPI } from "@blings/blings-player/editor-tools";
import type {
  LayerTextBoxResizeEvent,
  LayerTextConnectorActionEvent,
  LayerTextControlReportRequest,
  LayerTextEditEvent,
  MotionCopyLayersResult,
  MotionLayerClipboardSource,
  MotionMoveLayersOptions,
  MotionStructuralApplyResult,
  MotionStructuralOptions,
  MotionTargetQuery,
} from "@blings/blings-player/editor-tools";

import _ from "lodash";
import { autorun, toJS } from "mobx";
import {
  destroyPlayerSession,
  runPlayer as runPlayerSession,
  runPlayerDebounce as schedulePlayerSessionRun,
} from "./runPlayer";
import { getAllColors } from "./getAllColorsFronJson";
import { getAssetLayerPairs } from "./getAssetLayerPairs";
import { getFonts } from "./getFonts";
import {
  getLayerInspectorState,
  setLayerInspectorEnabled as setLayerInspectorEnabledHelper,
  setLayerInspectorSelectionCaptureEnabled as setLayerInspectorSelectionCaptureEnabledHelper,
  setLayerInspectorMode as setLayerInspectorModeHelper,
  setLayerInspectorLayerDoubleClickHandler,
  setLayerInspectorTextConnectorActionHandler,
  setLayerInspectorTextControlReportHandler,
  setLayerInspectorTextEditCommitHandler,
  setLayerInspectorTransformCommitHandler,
  subscribeToLayerInspector,
  selectLayerInInspector,
  selectLayerByUidInInspector,
  setLayerInspectorSelections as setLayerInspectorSelectionsInPlayer,
  clearLayerInspectorSelections as clearLayerInspectorSelectionsInPlayer,
} from "../helpers/layerInspectorTools";
import type {
  LayerInspectorSelection,
  LayerInspectorState,
  LayerInspectorContextMenuEvent,
  LayerInspectorMode,
} from "../helpers/layerInspectorTools";
import { recordStudioPerf, studioPerfNow } from "../helpers/studioPerfDebug";
import { normalizeProjectFontOverrideForPlayer } from "../helpers/fontOverrides";
import { getVideoPartFonts } from "../helpers/videoPartFonts";
import {
  buildPlaygroundAddLayerConfig,
  type PlaygroundAddLayerKind,
} from "../helpers/layerCreation";
import {
  createStructuralImpactReport,
  parseLayerClipboardText,
  prepareLayerClipboardPaste,
} from "../helpers/layerClipboard";
import {
  canApplyTextChangesToLayer,
  cloneJsonDraft,
  cloneVideoPartForPlayer,
  findDraftLayerTarget,
  findStoreModForLayerSelection,
  findStoreModForTextConnector,
  getRuntimeAnimationFromPlayer,
  getRuntimeControlReportMods,
  resolveTransformEventForDraft,
  setExistingPathValue,
} from "./playerDraftHelpers";

export const mainAssetKey = "main";
export const sequenceLayerId = "sequence_";
export const seqImgId = "imgSeq_";
const LAYER_INSPECTOR_HOVER_FLUSH_DELAY_MS = 50;

const areLayerInspectorSelectionsEqual = (
  first?: LayerInspectorSelection,
  second?: LayerInspectorSelection
): boolean => {
  if (first === second) {
    return true;
  }

  if (!first || !second) {
    return false;
  }

  const hasStableIdentity =
    Boolean(first.path || second.path) ||
    Boolean(first.instancePath || second.instancePath) ||
    Boolean(first.className || second.className) ||
    Boolean(first.layerName || second.layerName) ||
    first.layerUid !== undefined ||
    second.layerUid !== undefined ||
    Boolean(first.refId || second.refId) ||
    first.assetUid !== undefined ||
    second.assetUid !== undefined;

  if (!hasStableIdentity) {
    return false;
  }

  return (
    first.path === second.path &&
    first.instancePath === second.instancePath &&
    first.className === second.className &&
    first.assetName === second.assetName &&
    first.layerName === second.layerName &&
    first.layerUid === second.layerUid &&
    first.layerType === second.layerType &&
    first.refId === second.refId &&
    first.assetUid === second.assetUid
  );
};

const areLayerInspectorSelectionListsEqual = (
  first: LayerInspectorSelection[],
  second: LayerInspectorSelection[]
): boolean =>
  first.length === second.length &&
  first.every((selection, index) =>
    areLayerInspectorSelectionsEqual(selection, second[index])
  );

const getLayerInspectorSelectionDebugKey = (
  selection?: LayerInspectorSelection
): string => {
  if (!selection) return "";
  if (selection.instancePath) return `instance:${selection.instancePath}`;
  if (typeof selection.layerUid === "number") return `uid:${selection.layerUid}`;
  if (selection.path) return `path:${selection.path}`;
  if (selection.className) return `class:${selection.className}`;
  if (selection.layerName) {
    return `name:${selection.assetName ?? mainAssetKey}:${selection.layerName}`;
  }
  return "";
};

const requestLayerInspectorHoverFlush = (callback: () => void): number =>
  setTimeout(callback, LAYER_INSPECTOR_HOVER_FLUSH_DELAY_MS) as unknown as number;

const cancelLayerInspectorHoverFlush = (id: number): void => {
  clearTimeout(id);
};

type LayerSortAction =
  | "bringToFront"
  | "bringForward"
  | "sendBackward"
  | "sendToBack";

type LayerListPlacement = {
  container?: MotionMoveLayersOptions["container"];
  layerCount: number;
  layerIndex: number;
  layer: unknown;
  layers: unknown[];
  listPath: string;
};

function getSelectionStructuralTarget(
  animation: unknown,
  selection: LayerInspectorSelection
): MotionTargetQuery | undefined {
  const draftTarget = findDraftLayerTarget(animation, {
    path: selection.path,
    layerName: selection.layerName,
    layerUid: selection.layerUid,
    assetName: selection.assetName,
    assetId: selection.assetName,
  });

  if (draftTarget) {
    return {
      path: draftTarget.path,
      assetId: draftTarget.assetId,
      layerName: draftTarget.layerName,
      layerUid: draftTarget.layerUid,
    };
  }

  if (!selection.path && !selection.layerName && selection.layerUid == null) {
    return undefined;
  }

  return {
    path: selection.path || undefined,
    assetId: selection.assetName,
    layerName: selection.layerName,
    layerUid: selection.layerUid,
  };
}

function buildStructuralTargets(
  animation: unknown,
  selections: LayerInspectorSelection[]
): MotionTargetQuery[] {
  const seen = new Set<string>();
  return selections.reduce<MotionTargetQuery[]>((targets, selection) => {
    const target = getSelectionStructuralTarget(animation, selection);
    if (!target) return targets;

    const key =
      target.path ??
      [
        target.assetId ?? "main",
        target.layerUid ?? "",
        target.layerName ?? "",
      ].join("::");
    if (!key || seen.has(key)) return targets;

    seen.add(key);
    targets.push(target);
    return targets;
  }, []);
}

function getLayerListPlacement(
  animation: unknown,
  target: MotionTargetQuery
): LayerListPlacement | undefined {
  if (!target.path) return undefined;

  const rootMatch = /^layers\[(\d+)]$/.exec(target.path);
  if (rootMatch) {
    const layers = (animation as { layers?: unknown[] })?.layers;
    if (!Array.isArray(layers)) return undefined;

    const layerIndex = Number(rootMatch[1]);
    if (layerIndex < 0 || layerIndex >= layers.length) return undefined;

    return {
      layerCount: layers.length,
      layerIndex,
      layer: layers[layerIndex],
      layers,
      listPath: "layers",
    };
  }

  const assetMatch = /^assets\[(\d+)]\.layers\[(\d+)]$/.exec(target.path);
  if (!assetMatch) return undefined;

  const assetIndex = Number(assetMatch[1]);
  const layerIndex = Number(assetMatch[2]);
  const asset = (animation as {
    assets?: Array<{ id?: string; layers?: unknown[] }>;
  })?.assets?.[assetIndex];
  if (!Array.isArray(asset?.layers)) return undefined;
  if (layerIndex < 0 || layerIndex >= asset.layers.length) return undefined;

  const listPath = `assets[${assetIndex}].layers`;
  const assetId = asset.id ?? target.assetId;

  return {
    container: assetId ? { assetId } : { path: listPath },
    layerCount: asset.layers.length,
    layerIndex,
    layer: asset.layers[layerIndex],
    layers: asset.layers,
    listPath,
  };
}

function isMediaBackstopLayer(layer: unknown): boolean {
  const layerType = (layer as { ty?: unknown })?.ty;
  return (
    layerType === LayerType.video ||
    layerType === LayerType.pholderVideo ||
    layerType === LayerType.audio
  );
}

function getBackmostVisualInsertIndex(
  placement: LayerListPlacement,
  selectedIndexes: number[]
): number {
  const selectedIndexSet = new Set(selectedIndexes);
  const layersAfterRemoval = placement.layers.filter(
    (_layer, index) => !selectedIndexSet.has(index)
  );
  const firstMediaIndex = layersAfterRemoval.findIndex(isMediaBackstopLayer);

  return firstMediaIndex >= 0 ? firstMediaIndex : layersAfterRemoval.length;
}

function clampOrderToMediaBoundary(
  order: MotionMoveLayersOptions["order"],
  placement: LayerListPlacement,
  placements: LayerListPlacement[],
  selectedIndexes: number[]
): MotionMoveLayersOptions["order"] {
  if (order?.toIndex == null) {
    return order;
  }

  const includesVisualLayer = placements.some(
    (currentPlacement) => !isMediaBackstopLayer(currentPlacement.layer)
  );
  if (!includesVisualLayer) {
    return order;
  }

  return {
    toIndex: Math.min(
      order.toIndex,
      getBackmostVisualInsertIndex(placement, selectedIndexes)
    ),
  };
}

function getLayerSortMoveOptions(
  animation: unknown,
  targets: MotionTargetQuery[],
  action: LayerSortAction
): {
  blockedReason?: string;
  moveOptions?: Pick<MotionMoveLayersOptions, "container" | "order">;
} {
  const resolvedPlacements = targets.map((target) =>
    getLayerListPlacement(animation, target)
  );
  const firstPlacement = resolvedPlacements[0];

  if (
    !firstPlacement ||
    resolvedPlacements.some((placement) => !placement)
  ) {
    return {
      blockedReason: "The selected layer order could not be resolved.",
    };
  }

  const placements = resolvedPlacements as LayerListPlacement[];

  if (
    placements.some(
      (placement) => placement.listPath !== firstPlacement.listPath
    )
  ) {
    return {
      blockedReason:
        "Layer sorting can only be applied to layers in the same container.",
    };
  }

  const layerIndexes = placements.map((placement) => placement.layerIndex);
  const minIndex = Math.min(...layerIndexes);
  const maxIndex = Math.max(...layerIndexes);
  const layerCount = firstPlacement.layerCount;
  let order: MotionMoveLayersOptions["order"] = { toIndex: layerCount };
  switch (action) {
    case "bringToFront":
      order = { toIndex: 0 };
      break;
    case "bringForward":
      order = { toIndex: Math.max(0, minIndex - 1) };
      break;
    case "sendBackward":
      order = { toIndex: Math.min(layerCount, maxIndex + 1) };
      break;
    case "sendToBack":
      break;
  }
  order = clampOrderToMediaBoundary(
    order,
    firstPlacement,
    placements,
    layerIndexes
  );
  const moveOptions: Pick<MotionMoveLayersOptions, "container" | "order"> = {
    order,
  };

  if (firstPlacement.container) {
    moveOptions.container = firstPlacement.container;
  }

  return { moveOptions };
}

function toLayerInspectorSelectionFromPath(
  animation: unknown,
  path: string,
  fallback: LayerInspectorSelection
): LayerInspectorSelection {
  try {
    const resolved = motionAPI.inspect.resolveTarget(animation as any, {
      path,
    })[0];
    const resolvedLayer = resolved?.layer as
      | { ty?: number; refId?: string }
      | undefined;

    if (resolved) {
      return {
        ...fallback,
        path: resolved.path ?? path,
        assetName: resolved.assetName ?? resolved.assetId ?? fallback.assetName,
        layerName: resolved.layerName ?? fallback.layerName,
        layerUid: resolved.layerUid ?? fallback.layerUid,
        layerType: resolvedLayer?.ty ?? fallback.layerType,
        refId: resolvedLayer?.refId ?? fallback.refId,
      };
    }
  } catch {
    // Keep the path update even if target resolution fails.
  }

  return { ...fallback, path };
}

function buildMovedLayerInspectorSelections(
  result: MotionStructuralApplyResult,
  sourceTargets: MotionTargetQuery[],
  sourceSelections: LayerInspectorSelection[]
): LayerInspectorSelection[] {
  const movedPathByOldPath = new Map(
    result.pathChanges
      .filter((change) => change.status === "moved" && change.oldPath)
      .map((change) => [change.oldPath!, change.newPath ?? change.oldPath!])
  );

  return sourceTargets
    .map((target, index) => {
      const sourceSelection = sourceSelections[index];
      if (!sourceSelection) return undefined;

      const nextPath =
        target.path && movedPathByOldPath.has(target.path)
          ? movedPathByOldPath.get(target.path)
          : target.path ?? sourceSelection.path;

      return nextPath
        ? toLayerInspectorSelectionFromPath(
            result.animation,
            nextPath,
            sourceSelection
          )
        : sourceSelection;
    })
    .filter(
      (selection): selection is LayerInspectorSelection => Boolean(selection)
    );
}

function buildStructuralOptions(
  rootStore: RootInstance,
  frame: number
): MotionStructuralOptions {
  return {
    frame,
    mods: rootStore.modsStore.mods.map((mod) => toJS(mod.moddata)) as any,
    data:
      (toJS(rootStore.dynamicDataStore.perVideoCurrentData) as Record<
        string,
        unknown
      >) || {},
    liveControlData:
      (toJS(rootStore.dynamicDataStore.liveControlCurrentData) as Record<
        string,
        unknown
      >) || {},
  };
}

function getLayerClipboardSource(
  rootStore: RootInstance
): MotionLayerClipboardSource {
  const source: MotionLayerClipboardSource = {};
  const sceneName =
    rootStore.platformStore.selectedVideoPartName ??
    rootStore.platformStore.selectedSceneName;

  if (rootStore.platformStore.selectedProjectId) {
    source.projectId = rootStore.platformStore.selectedProjectId;
  }
  if (sceneName) {
    source.sceneName = sceneName;
  }

  return source;
}

function layerClipboardSourceMatchesCurrentScene(
  source: MotionLayerClipboardSource | undefined,
  currentSource: MotionLayerClipboardSource
): boolean {
  return Boolean(
    source?.projectId &&
      source.sceneName &&
      source.projectId === currentSource.projectId &&
      source.sceneName === currentSource.sceneName
  );
}

function getSameSceneClipboardConnectorActions(
  actions: MotionStructuralApplyResult["connectorActions"],
  payloadSource: MotionLayerClipboardSource,
  currentSource: MotionLayerClipboardSource
): MotionStructuralApplyResult["connectorActions"] {
  if (!layerClipboardSourceMatchesCurrentScene(payloadSource, currentSource)) {
    return [];
  }

  return actions.filter((action) =>
    layerClipboardSourceMatchesCurrentScene(
      action.clipboardSource ?? payloadSource,
      currentSource
    )
  );
}

function matchesStructuralTarget(
  change: MotionStructuralApplyResult["idChanges"][number],
  target?: MotionTargetQuery
): boolean {
  if (!target) return false;

  const uidMatches =
    typeof target.layerUid === "number" && change.oldUid === target.layerUid;
  const assetMatches =
    !target.assetId || !change.assetId || target.assetId === change.assetId;

  return (
    uidMatches ||
    (assetMatches &&
      Boolean(target.layerName) &&
      change.layerName === target.layerName)
  );
}

function getCreatedTargetForPrimary(
  result: MotionStructuralApplyResult,
  primaryTarget?: MotionTargetQuery
): MotionStructuralApplyResult["createdTargets"][number] | undefined {
  const primaryIdChange = result.idChanges.find((change) =>
    matchesStructuralTarget(change, primaryTarget)
  );
  const createdPath = primaryIdChange?.path;
  if (createdPath) {
    const createdTarget = result.createdTargets.find(
      (target) => target.path === createdPath
    );
    if (createdTarget) return createdTarget;
  }

  return result.createdTargets[result.createdTargets.length - 1];
}

function toLayerInspectorSelection(
  animation: unknown,
  target: MotionStructuralApplyResult["createdTargets"][number] | undefined
): LayerInspectorSelection | undefined {
  if (!target) return undefined;

  const resolved = motionAPI.inspect.resolveTarget(animation as any, {
    path: target.path,
  })[0];
  const resolvedLayer = resolved?.layer as
    | { ty?: number; refId?: string }
    | undefined;

  return {
    path: resolved?.path ?? target.path,
    assetName: resolved?.assetId ?? target.assetId,
    layerName: resolved?.layerName ?? target.layerName,
    layerUid: resolved?.layerUid ?? target.layerUid,
    layerType: resolvedLayer?.ty,
    refId: resolvedLayer?.refId,
  };
}

type RunPlayerOptions = {
  resetSession?: boolean;
  immediate?: boolean;
  startFrame?: number;
  animationChange?: unknown;
};
type ReplaceDraftJsonOptions = {
  suppressPlayerAutorun?: boolean;
};
type LayerTransformCommitEvent = Parameters<
  typeof inspectorAPI.applyLayerTransformChanges
>[1];
type LayerTextCommitEvent = LayerTextEditEvent | LayerTextBoxResizeEvent;
type StudioWindowWithPlayer = Window & {
  p?: { destroy?: () => void } | null;
};

const IMMEDIATE_LAYER_TRANSFORM_INTERACTIONS = [
  "drag",
  "move",
  "scale",
  "rotate",
];

function shouldRunLayerInspectorTransformImmediately(
  event: LayerTransformCommitEvent
): boolean {
  const interaction = (event as { interaction?: unknown }).interaction;
  return (
    typeof interaction === "string" &&
    IMMEDIATE_LAYER_TRANSFORM_INTERACTIONS.includes(interaction)
  );
}

export const PlayerModel = types
  .model({
    videoBgColor: types.optional(types.string, ""),
    jsonFile: types.maybe(types.frozen()),
    jsonFileName: types.maybe(types.string),
    loadedJsonSnapshot: types.maybe(types.frozen()),
    loadedJsonUrl: types.maybe(types.string),
    hasUnsavedJsonDraftChanges: types.optional(types.boolean, false),
    uploadedImages: types.array(
      types.model({
        name: types.string,
        url: types.string,
        blob: types.frozen(),
      }),
    ),
    autoplay: types.optional(types.boolean, false),
    posterFrame: types.optional(types.number, 1),
    fontConfig: types.optional(
      types.model({
        google: types.maybe(
          types.model({
            families: types.array(types.string),
          }),
        ),
        typekit: types.maybe(
          types.model({
            id: types.maybe(types.string),
            api: types.maybe(types.string),
          }),
        ),
        custom: types.maybe(
          types.model({
            families: types.array(types.string),
            urls: types.array(types.string),
          }),
        ),
      }),
      {},
    ),

    rendererType: types.optional(
      types.enumeration(["svg", "canvas", "html"]),
      "svg",
    ),
    assetLayerPairs: types.map(
      types.array(
        types.model({
          nm: types.maybeNull(types.string),
          uid: types.maybe(types.number),
        }),
      ),
    ),
    assetNames: types.array(types.string),
    allColors: types.array(types.array(types.number)),
    googleFonts: types.array(types.string),
    mainAssetKey: types.optional(types.string, "main"),
    currentFrame: types.optional(types.number, 0),
    totalFrames: types.optional(types.number, 0),
    resetScenesButtonEnabled: types.optional(types.boolean, false),
    playerLoadError: types.optional(types.string, ""),
    layerInspectorEnabled: types.optional(types.boolean, true),
    layerInspectorMode: types.optional(
      types.enumeration(["view", "design", "connect"]),
      "connect",
    ),
    layerInspectorAvailable: types.optional(types.boolean, false),
    layerInspectorContextMenu: types.maybe(
      types.frozen<LayerInspectorContextMenuEvent>(),
    ),
  })
  .volatile((self) => ({
    player: undefined as typeof BlingsPlayer | undefined,
    lastVPAndProject: "",
    layerInspectorUnsubscribe: undefined as (() => void) | undefined,
    layerInspectorSelections: [] as LayerInspectorSelection[],
    layerInspectorPrimarySelection: undefined as
      | LayerInspectorSelection
      | undefined,
    layerInspectorHoveredLayers: [] as LayerInspectorSelection[],
    layerInspectorHoveredPrimarySelection: undefined as
      | LayerInspectorSelection
      | undefined,
    jsonDownloadRequestId: 0,
    playerAutorunDisposer: undefined as (() => void) | undefined,
    suppressNextPlayerAutorun: false,
  }))
  .views((self) => ({
    get previewVideoPart(): (IVideoPart & { json?: unknown }) | undefined {
      if (!self.jsonFile) return undefined;
      const { platformStore, modsStore } = getRoot<RootInstance>(self);
      const selected = platformStore.selectedVideoPart;
      if (
        selected?.jsonUrl &&
        self.loadedJsonUrl !== selected.jsonUrl &&
        !self.hasUnsavedJsonDraftChanges
      ) return undefined;

      const existing = platformStore.projectWorkspaceVersion?.videoParts?.find(
        (vp) => vp.name === selected?.name
      ) as IVideoPart | undefined;
      return {
        ...(existing ? cloneVideoPartForPlayer(existing) : {}),
        name: selected?.name || "",
        json: self.jsonFile,
        // Saved fonts stay authoritative until the draft actually changes.
        ...(self.hasUnsavedJsonDraftChanges
          ? { fonts: getVideoPartFonts(self.jsonFile, existing?.fonts || []) }
          : {}),
        modsArr: modsStore.mods.map((mod) => cloneJsonDraft(mod.moddata) as any),
      };
    },
  }))
  .actions((self) => {
    let pendingLayerInspectorHoverState: LayerInspectorState | undefined;
    let pendingLayerInspectorHoverFrameId: number | undefined;

    return {
    syncJsonDirtyState() {
      self.hasUnsavedJsonDraftChanges = !_.isEqual(
        self.loadedJsonSnapshot,
        self.jsonFile
      );
    },
    consumeSuppressedPlayerAutorun() {
      if (!self.suppressNextPlayerAutorun) {
        return false;
      }

      self.suppressNextPlayerAutorun = false;
      return true;
    },
    applyJsonFileState(jsonFile, jsonFileName) {
      self.jsonFile = jsonFile;
      self.jsonFileName = jsonFileName;

      const { modsStore } = getRoot<RootInstance>(self);
      if (!jsonFile) {
        self.assetLayerPairs.clear();
        self.assetNames.clear();
        self.allColors.clear();
        self.googleFonts.clear();
        modsStore.checkAllModsForBrokenLayers();
        return;
      }

      const [assetLayerPairs, mainPairs] = getAssetLayerPairs(jsonFile);
      self.mainAssetKey = assetLayerPairs["main"]
        ? "main_" + Object.keys(assetLayerPairs).length
        : "main";
      assetLayerPairs[mainAssetKey] = mainPairs;
      Object.keys(assetLayerPairs).forEach((key) => {
        if (key.indexOf(sequenceLayerId) === 0) {
          delete assetLayerPairs[key];
        }
      });
      self.assetLayerPairs.replace(assetLayerPairs);
      const assetNames = jsonFile.assets
        .filter((a) => a.p)
        .filter((a) => a.id.indexOf(seqImgId) !== 0)
        .map((a) => a.p);
      self.assetNames.replace(assetNames);

      const allColors = getAllColors(jsonFile);
      self.allColors.replace(allColors);

      const googleFonts = getFonts(jsonFile);
      self.googleFonts.replace(googleFonts);
      modsStore.checkAllModsForBrokenLayers();
    },
    setJsonBaseline(jsonSnapshot = self.jsonFile, jsonUrl?: string) {
      self.loadedJsonSnapshot = cloneJsonDraft(jsonSnapshot);
      self.loadedJsonUrl = jsonUrl;
      this.syncJsonDirtyState();
    },
    setLastScene(vp: string | undefined, pid: string | null) {
      self.lastVPAndProject = JSON.stringify([vp, pid]);
    },
    setVideoBgColor(newColor) {
      self.videoBgColor = newColor;
    },
    updateFont(type, key, value) {
      if (!self.fontConfig[type]) {
        self.fontConfig[type] = {};
      }
      self.fontConfig[type][key] = value;
    },
    addFontConfigToMods() {
      const fontConfig = toJS(self.fontConfig);
      for (const key in fontConfig) {
        if (fontConfig.hasOwnProperty(key) && !fontConfig[key]) {
          delete fontConfig[key];
        }
      }
      const rootStore = getRoot<RootInstance>(self);
      rootStore.modsStore.addMod({
        type: modTypes.font,
        fontConfig: fontConfig,
      });
    },

    setUploadedImages(name, url, blob) {
      self.uploadedImages.push({ name, url, blob });
      this.runPlayer();
    },

    setJsonFile(
      jsonFile,
      jsonFileName,
      options?: { markAsLoaded?: boolean; jsonUrl?: string }
    ) {
      this.applyJsonFileState(jsonFile, jsonFileName);
      if (options?.markAsLoaded) {
        this.setJsonBaseline(jsonFile, options.jsonUrl);
        return;
      }
      this.syncJsonDirtyState();
    },

    replaceDraftJson(
      jsonFile,
      jsonFileName = self.jsonFileName,
      options?: ReplaceDraftJsonOptions
    ) {
      if (options?.suppressPlayerAutorun) {
        self.suppressNextPlayerAutorun = true;
      }
      this.applyJsonFileState(jsonFile, jsonFileName);
      this.syncJsonDirtyState();
    },

    applyJsonDraftSnapshot(jsonFile, jsonFileName = self.jsonFileName) {
      this.applyJsonFileState(cloneJsonDraft(jsonFile), jsonFileName);
      this.syncJsonDirtyState();
    },

    markCurrentJsonAsSaved(jsonUrl?: string) {
      this.setJsonBaseline(self.jsonFile, jsonUrl ?? self.loadedJsonUrl);
    },

    async downloadJsonFile(
      url: string,
      options?: { resetSession?: boolean }
    ) {
      const requestId = ++self.jsonDownloadRequestId;
      const raw = await fetch(url);
      const jvid = await raw.json();
      if (requestId !== self.jsonDownloadRequestId) {
        return;
      }
      const json = expand(jvid);
      if (requestId !== self.jsonDownloadRequestId) {
        return;
      }

      this.setJsonFile(json, url.split("/").slice(-1)[0], {
        markAsLoaded: true,
        jsonUrl: url,
      });
      if (options?.resetSession) {
        this.runPlayer({ resetSession: true, immediate: true });
      }
    },

    setCurrentFrame(frame: number, totalFrames: number) {
      const startedAt = studioPerfNow();
      const previousFrame = self.currentFrame;
      const previousTotalFrames = self.totalFrames;
      self.currentFrame = frame;
      self.totalFrames = totalFrames;
      recordStudioPerf(
        "PlayerStore.setCurrentFrame",
        studioPerfNow() - startedAt,
        {
          frame: Math.round(frame),
          frameChanged: previousFrame !== frame,
          totalFrames,
          totalFramesChanged: previousTotalFrames !== totalFrames,
        }
      );
    },
    setPlayer(player?: typeof BlingsPlayer) {
      self.player = player;
    },
    addLayerToCurrentFrame(kind: PlaygroundAddLayerKind) {
      if (!self.jsonFile) {
        return;
      }

      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();

      try {
        const nextJson = cloneJsonDraft(self.jsonFile);
        const config = buildPlaygroundAddLayerConfig({
          kind,
          animation: nextJson as any,
          currentFrame: self.currentFrame,
        });
        const result = motionAPI.apply.addLayer(nextJson as any, config);

        if (!result.layer || !result.layerPath || !result.changes.length) {
          console.warn("[AddLayer] failed to add layer", {
            kind,
            blockedReason: result.blockedReason,
          });
          return;
        }

        const addedLayer = result.layer as {
          nm?: string;
          uid?: number;
          ty?: number;
          refId?: string;
        };
        const selection: LayerInspectorSelection = {
          path: result.layerPath,
          layerName: addedLayer.nm,
          layerUid: addedLayer.uid,
          layerType: addedLayer.ty,
          refId: addedLayer.refId,
        };
        const startFrame = config.timing?.startFrame ?? self.currentFrame;

        this.setLayerInspectorEnabled(true);
        self.layerInspectorSelections = [selection];
        self.layerInspectorPrimarySelection = selection;
        this.replaceDraftJson(result.animation, self.jsonFileName, {
          suppressPlayerAutorun: true,
        });
        this.runPlayer({
          immediate: true,
          startFrame,
          animationChange: result.animation,
        });
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    setSingleLayerInspectorSelection(selection: LayerInspectorSelection) {
      this.setLayerInspectorEnabled(true);
      self.layerInspectorSelections = [selection];
      self.layerInspectorPrimarySelection = selection;
    },
    setLayerInspectorSelectionList(selections: LayerInspectorSelection[]) {
      if (!selections.length) {
        return;
      }

      this.setLayerInspectorEnabled(true);
      self.layerInspectorSelections = selections;
      self.layerInspectorPrimarySelection = selections[selections.length - 1];
    },
    async previewDeleteLayerSelections(
      selections?: LayerInspectorSelection[]
    ): Promise<MotionStructuralApplyResult | undefined> {
      if (!self.jsonFile) {
        return undefined;
      }

      const selectionList = selections?.length
        ? selections
        : self.layerInspectorSelections;
      const targets = buildStructuralTargets(self.jsonFile, selectionList);
      if (!targets.length) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);
      const previewAnimation = cloneJsonDraft(self.jsonFile);
      return motionAPI.apply.deleteLayers(previewAnimation as any, targets, {
        ...buildStructuralOptions(rootStore, self.currentFrame),
      });
    },
    async deleteLayerSelections(
      selections?: LayerInspectorSelection[]
    ): Promise<MotionStructuralApplyResult | undefined> {
      if (!self.jsonFile) {
        return undefined;
      }

      const selectionList = selections?.length
        ? selections
        : self.layerInspectorSelections;
      const targets = buildStructuralTargets(self.jsonFile, selectionList);
      if (!targets.length) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();

      try {
        const nextJson = cloneJsonDraft(self.jsonFile);
        const result = await motionAPI.apply.deleteLayers(
          nextJson as any,
          targets,
          buildStructuralOptions(rootStore, self.currentFrame)
        );

        if (result.blockedReason || !result.changes.length) {
          console.warn("[LayerActions] failed to delete layers", {
            blockedReason: result.blockedReason,
            targets,
          });
          return result;
        }

        this.setLayerInspectorEnabled(true, { clearSelections: true });
        this.replaceDraftJson(result.animation, self.jsonFileName, {
          suppressPlayerAutorun: true,
        });
        this.runPlayer({
          immediate: true,
          startFrame: self.currentFrame,
          animationChange: result.animation,
        });

        return result;
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    async copyLayerSelections(
      selections?: LayerInspectorSelection[]
    ): Promise<MotionCopyLayersResult | undefined> {
      if (!self.jsonFile) {
        return undefined;
      }

      const selectionList = selections?.length
        ? selections
        : self.layerInspectorSelections;
      const targets = buildStructuralTargets(self.jsonFile, selectionList);
      if (!targets.length) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);

      const result = await motionAPI.apply.copyLayers(
        self.jsonFile as any,
        targets,
        {
          ...buildStructuralOptions(rootStore, self.currentFrame),
          source: getLayerClipboardSource(rootStore),
        }
      );

      if (result.blockedReason || !result.clipboardPayload.layers.length) {
        console.warn("[LayerActions] failed to copy layers", {
          blockedReason: result.blockedReason,
          targets,
        });
      }

      return result;
    },
    async pasteLayerClipboardText(
      clipboardText?: string
    ): Promise<MotionStructuralApplyResult | undefined> {
      const payload = parseLayerClipboardText(clipboardText);
      if (!self.jsonFile || !payload || !payload.layers.length) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();

      try {
        const currentSource = getLayerClipboardSource(rootStore);
        const pasteResult = prepareLayerClipboardPaste(self.jsonFile, payload, {
          currentFrame: self.currentFrame,
        });
        if (!pasteResult) {
          return undefined;
        }

        const nextJson = pasteResult.animation;
        const pastedSelections = pasteResult.createdTargets
          .map((target) => toLayerInspectorSelection(nextJson, target))
          .filter(
            (selection): selection is LayerInspectorSelection =>
              Boolean(selection)
          );
        const connectorActions = getSameSceneClipboardConnectorActions(
          pasteResult.connectorActions,
          payload.source,
          currentSource
        );
        const impactReport = createStructuralImpactReport(connectorActions);

        this.replaceDraftJson(nextJson, self.jsonFileName, {
          suppressPlayerAutorun: true,
        });
        if (connectorActions.length) {
          rootStore.modsStore.addDuplicateConnectorTargets(connectorActions);
        }
        if (pastedSelections.length) {
          this.setLayerInspectorSelectionList(pastedSelections);
        }
        this.runPlayer({
          immediate: true,
          startFrame: self.currentFrame,
          animationChange: nextJson,
        });

        return {
          animation: nextJson as MotionStructuralApplyResult["animation"],
          changeSet: {
            version: 1,
            animationFrameRate: Number((nextJson as { fr?: number }).fr) || 0,
            changes: pasteResult.changes,
          },
          targets: [],
          changes: pasteResult.changes,
          affectedTargets: cloneJsonDraft(payload.affectedTargets),
          createdTargets: pasteResult.createdTargets,
          pathChanges: [],
          idChanges: [],
          connectorActions,
          impactReport,
          importedAssetIds: pasteResult.importedAssetIds,
          warnings: cloneJsonDraft(payload.warnings || []),
        };
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    async duplicateLayerSelections(
      selections?: LayerInspectorSelection[],
      primarySelection?: LayerInspectorSelection
    ): Promise<MotionStructuralApplyResult | undefined> {
      if (!self.jsonFile) {
        return undefined;
      }

      const selectionList = selections?.length
        ? selections
        : self.layerInspectorSelections;
      const targets = buildStructuralTargets(self.jsonFile, selectionList);
      if (!targets.length) {
        return undefined;
      }

      const primaryTarget = buildStructuralTargets(self.jsonFile, [
        primarySelection ??
          self.layerInspectorPrimarySelection ??
          selectionList[selectionList.length - 1],
      ].filter((selection): selection is LayerInspectorSelection =>
        Boolean(selection)
      ))[0];
      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();

      try {
        const nextJson = cloneJsonDraft(self.jsonFile);
        const result = await motionAPI.apply.duplicateLayers(
          nextJson as any,
          targets,
          buildStructuralOptions(rootStore, self.currentFrame)
        );

        if (result.blockedReason || !result.changes.length) {
          console.warn("[LayerActions] failed to duplicate layers", {
            blockedReason: result.blockedReason,
            targets,
          });
          return result;
        }

        const duplicatedSelection = toLayerInspectorSelection(
          result.animation,
          getCreatedTargetForPrimary(result, primaryTarget)
        );
        if (duplicatedSelection) {
          this.setSingleLayerInspectorSelection(duplicatedSelection);
        }

        this.replaceDraftJson(result.animation, self.jsonFileName, {
          suppressPlayerAutorun: true,
        });
        rootStore.modsStore.addDuplicateConnectorTargets(
          result.connectorActions
        );
        this.runPlayer({
          immediate: true,
          startFrame: self.currentFrame,
          animationChange: result.animation,
        });

        return result;
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    async moveLayerTargets(
      targets: MotionTargetQuery[],
      moveOptions: Pick<
        MotionMoveLayersOptions,
        "container" | "order" | "reparent"
      > = {}
    ): Promise<MotionStructuralApplyResult | undefined> {
      if (!self.jsonFile || !targets.length) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();

      try {
        const nextJson = cloneJsonDraft(self.jsonFile);
        const result = await motionAPI.apply.moveLayers(
          nextJson as any,
          targets,
          {
            ...buildStructuralOptions(rootStore, self.currentFrame),
            ...moveOptions,
          }
        );

        if (result.blockedReason || !result.changes.length) {
          console.warn("[LayerActions] failed to move layers", {
            blockedReason: result.blockedReason,
            targets,
            moveOptions,
          });
          return result;
        }

        this.replaceDraftJson(result.animation, self.jsonFileName, {
          suppressPlayerAutorun: true,
        });
        rootStore.modsStore.retargetMovedConnectorTargets(
          result.connectorActions
        );
        this.runPlayer({
          immediate: true,
          startFrame: self.currentFrame,
          animationChange: result.animation,
        });

        return result;
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    async sortLayerSelections(
      action: LayerSortAction,
      selections?: LayerInspectorSelection[]
    ): Promise<MotionStructuralApplyResult | undefined> {
      if (!self.jsonFile) {
        return undefined;
      }

      const selectionList = selections?.length
        ? selections
        : self.layerInspectorSelections;
      const targets = buildStructuralTargets(self.jsonFile, selectionList);
      if (!targets.length) {
        return undefined;
      }

      const sortMove = getLayerSortMoveOptions(
        self.jsonFile,
        targets,
        action
      );
      if (!sortMove.moveOptions) {
        console.warn("[LayerActions] failed to sort layers", {
          blockedReason: sortMove.blockedReason,
          targets,
          action,
        });
        return {
          blockedReason: sortMove.blockedReason,
        } as MotionStructuralApplyResult;
      }

      const result = await this.moveLayerTargets(targets, sortMove.moveOptions);
      if (!result || result.blockedReason || !result.changes.length) {
        return result;
      }

      const refreshedSelections = buildMovedLayerInspectorSelections(
        result,
        targets,
        selectionList
      );
      if (refreshedSelections.length) {
        this.setLayerInspectorSelectionList(refreshedSelections);
      }

      return result;
    },
    syncLayerInspectorSelectionToPlayer() {
      if (!self.layerInspectorEnabled || !self.layerInspectorSelections.length) {
        return;
      }

      const selections = toJS(self.layerInspectorSelections);
      const applySelection = () => {
        setLayerInspectorSelectionsInPlayer(selections);
      };

      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(applySelection);
      } else {
        setTimeout(applySelection, 0);
      }
    },

    runPlayer(options: RunPlayerOptions = {}) {
      const {
        resetSession = false,
        immediate = false,
        startFrame,
        animationChange,
      } = options;
      this.hideResetScenesButton();
      const {
        jsonFile,
        autoplay,
        posterFrame,
        // fontConfig,
        rendererType,
        uploadedImages,
      } = self;
      if (!jsonFile) {
        return;
      }

      const rootStore = getRoot<RootInstance>(self);
      const videoData = rootStore.dynamicDataStore.perVideoCurrentData;
      const videoPart = self.previewVideoPart;
      if (!videoPart) return;

      let playerVersion = -1;
      if (rootStore.platformStore.selectedProjectBasicInfo) {
        const playerVersionToUse =
          rootStore.platformStore.selectedProjectBasicInfo.playerVersionToUse;
        if (playerVersionToUse) {
          playerVersion = parseInt(playerVersionToUse.split("-")[0]);
        }
      }

      const JsonConfig = rootStore.modsStore.JsonConfig.map((mod) =>
        JSON.parse(mod.dataStr),
      );

      const jsonForPlayer = self.jsonFile;

      const workspaceVideoParts =
        rootStore.platformStore.projectWorkspaceVersion?.videoParts;
      /** videoParts should include all scenes, for dynamic-concat update */
      const videoParts: Array<IVideoPart & { json?: unknown }> =
        workspaceVideoParts?.map((vp) =>
          vp.name === videoPart.name ? videoPart : cloneVideoPartForPlayer(vp)
        ) || [videoPart];
      const experiments = rootStore.experimentStore.experiments;
      const variantSelectionSet = rootStore.experimentStore.selectedVariantSet;

      const vpidx = videoParts.findIndex((vp) => vp.name === videoPart.name);
      if (vpidx >= 0) videoParts[vpidx] = videoPart;
      else videoParts.push(videoPart);
      const liveControlData = rootStore.dynamicDataStore.liveControlCurrentData;
      const projectId = rootStore.platformStore.selectedProjectId;
      const flowDiagram =
        rootStore.platformStore.projectWorkspaceVersion?.flowDiagram;
      const fontOverrides =
        rootStore.platformStore.projectWorkspaceVersion?.fontOverrides?.map(
          normalizeProjectFontOverrideForPlayer,
        );
      rootStore.modsStore.clearPlayerReportedErrors();
      const runPlayerWithConfig = immediate
        ? runPlayerSession
        : schedulePlayerSessionRun;

      if (immediate) {
        schedulePlayerSessionRun.cancel();
      }

      self.playerLoadError = "";
      runPlayerWithConfig({
        projectId,
        jsonFile: jsonForPlayer,
        autoplay,
        posterFrame,
        startFrame,
        JsonConfig,
        rendererType,
        videoData,
        liveControlData,
        videoParts,
        scenes: [videoPart.name],
        experiments,
        variantSelectionSet,
        assetChanges: uploadedImages
          ? uploadedImages.reduce((o, ui) => {
              o[ui.name] = ui.url;
              return o;
            }, {})
          : [],

        onError: () => {
          this.setPlayerLoadError(
            "This scene could not load. Try again or check its connectors.",
          );
        },
        cb: (p) => {
          this.setPlayerLoadError("");
          if (p) {
            p.EE.on("onFrame", (f) => {
              try {
                const startedAt = studioPerfNow();
                const totalFrames = p.getTotalFrames();
                recordStudioPerf(
                  "Player.onFrame getTotalFrames",
                  studioPerfNow() - startedAt,
                  {
                    frame: Number.isFinite(f) ? Math.round(f) : String(f),
                    totalFrames,
                  }
                );
                this.setCurrentFrame(f, totalFrames);
              } catch (e) {
                console.error("err", e);
              }
            });
            p.EE.on("onAllReady", () => {
              try {
                this.setCurrentFrame(
                  p.animation?.currentFrame ?? 0,
                  p.getTotalFrames()
                );
              } catch (e) {
                console.error("err", e);
              }
            });
            p.EE.on("onReplaceAnimation", () => {
              this.showResetScenesButton();
            });
            p.EE.on("onError", () => {
              rootStore.modsStore.checkAllModsForBrokenLayers();
            });
          }
          this.setPlayer(p);
          this.syncLayerInspectorSelectionToPlayer();
        },
        playerVersion,
        forceRecreate: resetSession,
        flowDiagram,
        fontOverrides,
        animationChange,
      });
    },
    setPlayerLoadError(message: string) {
      self.playerLoadError = message;
    },
    resetScenePlayer() {
      this.runPlayer({ resetSession: true, immediate: true });
    },
    removePlayer() {
      self.playerLoadError = "";
      self.jsonDownloadRequestId += 1;
      this.applyJsonFileState("", "");
      self.loadedJsonSnapshot = undefined;
      self.loadedJsonUrl = undefined;
      self.hasUnsavedJsonDraftChanges = false;
      destroyPlayerSession();
      const studioWindow = window as StudioWindowWithPlayer;
      if (studioWindow.p) {
        try {
          studioWindow.p.destroy?.();
          studioWindow.p = null;
        } catch (e) {
          console.error("err destroying player", e);
        }
      }
    },
    showResetScenesButton() {
      self.resetScenesButtonEnabled = true;
    },
    hideResetScenesButton() {
      self.resetScenesButtonEnabled = false;
    },
    isLayerInspectorHoverOnlyState(state: LayerInspectorState) {
      if (
        state.available !== self.layerInspectorAvailable ||
        state.enabled !== self.layerInspectorEnabled ||
        state.mode !== self.layerInspectorMode ||
        state.contextMenu ||
        state.contextMenuDismissId
      ) {
        return false;
      }

      const selections = state.selections ?? [];
      return (
        areLayerInspectorSelectionListsEqual(
          self.layerInspectorSelections,
          selections
        ) &&
        areLayerInspectorSelectionsEqual(
          self.layerInspectorPrimarySelection,
          state.primarySelection
        )
      );
    },
    cancelPendingLayerInspectorHoverState() {
      if (pendingLayerInspectorHoverFrameId !== undefined) {
        cancelLayerInspectorHoverFlush(pendingLayerInspectorHoverFrameId);
      }
      pendingLayerInspectorHoverFrameId = undefined;
      pendingLayerInspectorHoverState = undefined;
    },
    flushPendingLayerInspectorHoverState() {
      const startedAt = studioPerfNow();
      const state = pendingLayerInspectorHoverState;
      pendingLayerInspectorHoverFrameId = undefined;
      pendingLayerInspectorHoverState = undefined;

      if (!state) {
        return;
      }

      this.updateLayerInspectorState(state);
      recordStudioPerf(
        "PlayerStore.flushLayerInspectorHoverState",
        studioPerfNow() - startedAt,
        {
          hoverCount: state.hoveredLayers?.length ?? 0,
          mode: state.mode,
          nextHoverKey: getLayerInspectorSelectionDebugKey(
            state.hoveredPrimarySelection
          ),
          selectionCount: state.selections?.length ?? 0,
        }
      );
    },
    scheduleLayerInspectorHoverState(state: LayerInspectorState) {
      pendingLayerInspectorHoverState = state;
      if (pendingLayerInspectorHoverFrameId !== undefined) {
        return;
      }

      pendingLayerInspectorHoverFrameId = requestLayerInspectorHoverFlush(() => {
        this.flushPendingLayerInspectorHoverState();
      });
    },
    receiveLayerInspectorState(state: LayerInspectorState) {
      const startedAt = studioPerfNow();
      const shouldScheduleHover = this.isLayerInspectorHoverOnlyState(state);

      if (shouldScheduleHover) {
        this.scheduleLayerInspectorHoverState(state);
        recordStudioPerf(
          "PlayerStore.receiveLayerInspectorState scheduled-hover",
          studioPerfNow() - startedAt,
          {
            hoverCount: state.hoveredLayers?.length ?? 0,
            mode: state.mode,
            nextHoverKey: getLayerInspectorSelectionDebugKey(
              state.hoveredPrimarySelection
            ),
            selectionCount: state.selections?.length ?? 0,
          }
        );
        return;
      }

      this.cancelPendingLayerInspectorHoverState();
      this.updateLayerInspectorState(state);
      recordStudioPerf(
        "PlayerStore.receiveLayerInspectorState immediate",
        studioPerfNow() - startedAt,
        {
          contextMenu: Boolean(state.contextMenu),
          hoverCount: state.hoveredLayers?.length ?? 0,
          mode: state.mode,
          nextSelectionKey: getLayerInspectorSelectionDebugKey(
            state.primarySelection
          ),
          selectionCount: state.selections?.length ?? 0,
        }
      );
    },
    updateLayerInspectorState(state: LayerInspectorState) {
      const startedAt = studioPerfNow();
      const previousPrimarySelection = self.layerInspectorPrimarySelection;
      const previousHoveredPrimarySelection =
        self.layerInspectorHoveredPrimarySelection;
      self.layerInspectorAvailable = state.available;
      self.layerInspectorEnabled = state.enabled;
      self.layerInspectorMode = state.mode;
      const selections = state.selections ?? [];
      const selectionChanged =
        !areLayerInspectorSelectionListsEqual(
          self.layerInspectorSelections,
          selections
        ) ||
        !areLayerInspectorSelectionsEqual(
          self.layerInspectorPrimarySelection,
          state.primarySelection
        );
      if (selectionChanged) {
        self.layerInspectorSelections = selections;
        self.layerInspectorPrimarySelection = state.primarySelection;
      }
      const hoveredLayers = state.hoveredLayers ?? [];
      const hoverChanged =
        !areLayerInspectorSelectionListsEqual(
          self.layerInspectorHoveredLayers,
          hoveredLayers
        ) ||
        !areLayerInspectorSelectionsEqual(
          self.layerInspectorHoveredPrimarySelection,
          state.hoveredPrimarySelection
        );
      if (hoverChanged) {
        self.layerInspectorHoveredLayers = hoveredLayers;
        self.layerInspectorHoveredPrimarySelection =
          state.hoveredPrimarySelection;
      }
      if (state.contextMenu) {
        self.layerInspectorContextMenu = state.contextMenu;
      } else if (state.contextMenuDismissId) {
        self.layerInspectorContextMenu = undefined;
      }
      recordStudioPerf(
        `PlayerStore.updateLayerInspectorState selection-${
          selectionChanged ? "changed" : "same"
        } hover-${hoverChanged ? "changed" : "same"}`,
        studioPerfNow() - startedAt,
        {
          attached: state.attached,
          contextMenu: Boolean(state.contextMenu),
          hoverCount: hoveredLayers.length,
          mode: state.mode,
          nextHoverKey: getLayerInspectorSelectionDebugKey(
            state.hoveredPrimarySelection
          ),
          nextSelectionKey: getLayerInspectorSelectionDebugKey(
            state.primarySelection
          ),
          previousHoverKey: getLayerInspectorSelectionDebugKey(
            previousHoveredPrimarySelection
          ),
          previousSelectionKey: getLayerInspectorSelectionDebugKey(
            previousPrimarySelection
          ),
          selectionCount: selections.length,
        }
      );
    },
    setLayerInspectorEnabled(
      value: boolean,
      options?: { clearSelections?: boolean },
    ) {
      if (options?.clearSelections) {
        self.layerInspectorSelections = [];
        self.layerInspectorPrimarySelection = undefined;
        self.layerInspectorHoveredLayers = [];
        self.layerInspectorHoveredPrimarySelection = undefined;
        self.layerInspectorContextMenu = undefined;
        clearLayerInspectorSelectionsInPlayer();
      }
      self.layerInspectorEnabled = value;
      self.layerInspectorMode = value ? "design" : "view";
      setLayerInspectorEnabledHelper(value);
    },
    setLayerInspectorSelectionCaptureEnabled(value: boolean) {
      setLayerInspectorSelectionCaptureEnabledHelper(value);
    },
    setLayerInspectorMode(
      mode: LayerInspectorMode,
      options?: { clearSelections?: boolean }
    ) {
      if (mode === "view" || options?.clearSelections) {
        self.layerInspectorSelections = [];
        self.layerInspectorPrimarySelection = undefined;
        self.layerInspectorHoveredLayers = [];
        self.layerInspectorHoveredPrimarySelection = undefined;
        self.layerInspectorContextMenu = undefined;
        clearLayerInspectorSelectionsInPlayer();
      }
      self.layerInspectorMode = mode;
      self.layerInspectorEnabled = mode !== "view";
      setLayerInspectorModeHelper(mode);
    },
    handleLayerInspectorTransformCommit(event: LayerTransformCommitEvent) {
      if (!self.jsonFile || !event.changes.length) {
        return false;
      }

      const rootStore = getRoot<RootInstance>(self);
      rootStore.undoStore.onGestureStart();
      try {
        const baseAnimation = cloneJsonDraft(self.jsonFile);
        const draftEvent = resolveTransformEventForDraft(baseAnimation, event);
        const preservedResult = inspectorAPI.applyLayerTransformChanges(
          baseAnimation as any,
          draftEvent,
        );
        const result = preservedResult.failures.length
          ? inspectorAPI.applyLayerTransformChanges(
              cloneJsonDraft(self.jsonFile) as any,
              draftEvent,
              { logger: console, mode: "replace" },
            )
          : preservedResult;

        if (result.failures.length) {
          console.error("Transform JSON update failed", result.failures);
          return false;
        }

        if (result.applied.length) {
          const immediate = shouldRunLayerInspectorTransformImmediately(event);
          this.replaceDraftJson(result.animation, self.jsonFileName, {
            suppressPlayerAutorun: true,
          });
          this.runPlayer({
            immediate,
            startFrame: event.frame,
            animationChange: result.animation,
          });
          return true;
        }

        return false;
      } finally {
        rootStore.undoStore.onGestureEnd();
      }
    },
    async getLayerInspectorTextControlReport(
      request: LayerTextControlReportRequest
    ) {
      if (!self.jsonFile) {
        return undefined;
      }

      const rootStore = getRoot<RootInstance>(self);
      const target = {
        path: request.target.path,
        layerName: request.target.layerName,
        layerUid: request.target.layerUid,
        assetId: request.target.assetName,
      };
      const fallbackMods = rootStore.modsStore.mods.map((mod) =>
        toJS(mod.moddata)
      );
      const fallbackModIds = rootStore.modsStore.mods.map((mod) => mod.id);
      const controlReportMods = getRuntimeControlReportMods(
        self.player,
        fallbackMods,
        fallbackModIds
      );
      const runtimeAnimation = getRuntimeAnimationFromPlayer(self.player);
      const reportAnimation = runtimeAnimation ?? self.jsonFile;
      const draftTarget = runtimeAnimation
        ? undefined
        : findDraftLayerTarget(self.jsonFile, target);
      const reportTarget = draftTarget
        ? {
            path: draftTarget.path,
            layerName: draftTarget.layerName,
            layerUid: draftTarget.layerUid,
            assetId: draftTarget.assetId,
            assetName: draftTarget.assetName,
          }
        : target;
      const options = {
        frame: request.frame,
        mods: controlReportMods.mods as any,
        data:
          (toJS(rootStore.dynamicDataStore.perVideoCurrentData) as Record<
            string,
            unknown
          >) || {},
        liveControlData:
          (toJS(rootStore.dynamicDataStore.liveControlCurrentData) as Record<
            string,
            unknown
          >) || {},
      };

      try {
        const reports = await motionAPI.inspect.getLayerControlReport(
          reportAnimation as any,
          reportTarget,
          options
        );
        return reports[0];
      } catch (error) {
        console.warn("[LayerInspectorText] text control report failed", error);
        return undefined;
      }
    },
    handleLayerInspectorTextCommit(event: LayerTextCommitEvent) {
      if (!self.jsonFile || !event.changes.length) {
        return;
      }

      const rootStore = getRoot<RootInstance>(self);
      const nextJson = cloneJsonDraft(self.jsonFile);
      const layerPath = event.target.path;
      const eventPathLayer = _.get(nextJson, layerPath);
      const draftTarget = findDraftLayerTarget(nextJson, event.target);
      const draftLayer = draftTarget
        ? _.get(nextJson, draftTarget.path)
        : undefined;
      const shouldUseDraftLayer =
        draftTarget &&
        draftLayer &&
        draftLayer !== eventPathLayer &&
        !canApplyTextChangesToLayer(eventPathLayer, event.changes) &&
        canApplyTextChangesToLayer(draftLayer, event.changes);
      const layer = shouldUseDraftLayer ? draftLayer : eventPathLayer;
      const resolvedLayerPath = shouldUseDraftLayer
        ? draftTarget.path
        : layerPath;
      if (!layer) {
        console.error("[LayerInspectorText] text JSON update failed", {
          reason: "event.target.path did not resolve in the draft JSON",
          target: event.target,
          frame: event.frame,
          changes: event.changes,
          layerPath,
          draftTarget,
        });
        return;
      }

      let appliedCount = 0;
      event.changes.forEach((change) => {
        if (setExistingPathValue(layer, change.path, change.value)) {
          appliedCount += 1;
        }
      });

      if (!appliedCount) {
        console.error(
          "[LayerInspectorText] text JSON update did not apply changes",
          {
            reason:
              "target layer resolved, but none of the editor-tools text change paths exist under that layer",
            target: event.target,
            frame: event.frame,
            layerPath: resolvedLayerPath,
            eventLayerPath: layerPath,
            draftTarget,
            textDocument: event.textDocument,
            changes: event.changes,
          }
        );
        return;
      }

      rootStore.undoStore.pushSnapshot();
      this.replaceDraftJson(nextJson, self.jsonFileName, {
        suppressPlayerAutorun: true,
      });
      this.runPlayer({
        startFrame: event.frame,
        animationChange: nextJson,
      });
    },
    handleLayerInspectorTextConnectorAction(
      event: LayerTextConnectorActionEvent
    ) {
      const rootStore = getRoot<RootInstance>(self);
      const fallbackMods = rootStore.modsStore.mods.map((mod) =>
        toJS(mod.moddata)
      );
      const fallbackModIds = rootStore.modsStore.mods.map((mod) => mod.id);
      const controlReportMods = getRuntimeControlReportMods(
        self.player,
        fallbackMods,
        fallbackModIds
      );
      const modId = controlReportMods.modIds[event.connector.modIndex];
      const indexedMod =
        typeof modId !== "undefined"
          ? rootStore.modsStore.mods.find(
              (candidate) => candidate.id.toString() === modId.toString()
            )
          : rootStore.modsStore.mods[event.connector.modIndex];
      const matchedMod =
        indexedMod ??
        findStoreModForTextConnector(rootStore.modsStore.mods, event.connector);

      if (!matchedMod && typeof modId === "undefined") {
        console.warn("Could not find text connector mod", event.connector);
        return;
      }

      if (!matchedMod) {
        console.warn("Could not find text connector mod by id", {
          connector: event.connector,
          modId,
        });
        return;
      }

      rootStore.modsStore.setActiveMod(matchedMod.id);
    },
    handleLayerInspectorLayerDoubleClick(selection: LayerInspectorSelection) {
      const rootStore = getRoot<RootInstance>(self);
      const matchedMod = findStoreModForLayerSelection(
        rootStore.modsStore.mods,
        selection,
        self.jsonFile,
        mainAssetKey
      );

      if (!matchedMod) {
        return;
      }

      rootStore.modsStore.setActiveMod(matchedMod.id);
    },
    toggleLayerInspector() {
      this.setLayerInspectorEnabled(!self.layerInspectorEnabled);
    },
    hideLayerInspectorContextMenu() {
      self.layerInspectorContextMenu = undefined;
    },
    showLayerInspectorContextMenu(
      position: {
        clientX: number;
        clientY: number;
        pageX: number;
        pageY: number;
      },
      selections: LayerInspectorSelection[],
      primarySelection?: LayerInspectorSelection
    ) {
      if (!selections.length) return;
      const primary = primarySelection ?? selections[selections.length - 1];
      self.layerInspectorContextMenu = {
        id: Date.now(),
        position,
        layer: primary,
        selections: selections.map((selection) => ({ ...selection })),
      };
    },
    selectLayerInVideo(layerName: string, assetName?: string) {
      if (!self.layerInspectorEnabled) return;
      selectLayerInInspector(layerName, assetName);
    },
    selectLayerByUidInVideo(
      layerUid: number,
      assetId?: string,
      assetName?: string,
      layerNm?: string,
      layerPath?: string
    ) {
      if (!self.layerInspectorEnabled) return;
      selectLayerByUidInInspector(
        layerUid,
        assetId,
        assetName,
        layerNm,
        layerPath
      );
    },
  };
  })
  .actions((self) => ({
    afterAttach() {
      self.playerAutorunDisposer?.();
      self.playerAutorunDisposer = autorun(() => {
        if (self.jsonFile || self.uploadedImages.length > 0) {
          if (self.consumeSuppressedPlayerAutorun()) {
            return;
          }
          self.runPlayer();
        }
      });

      self.updateLayerInspectorState(getLayerInspectorState());
      setLayerInspectorTransformCommitHandler((event) =>
        self.handleLayerInspectorTransformCommit(event),
      );
      setLayerInspectorTextControlReportHandler((request) =>
        self.getLayerInspectorTextControlReport(request),
      );
      setLayerInspectorTextEditCommitHandler((event) =>
        self.handleLayerInspectorTextCommit(event),
      );
      setLayerInspectorTextConnectorActionHandler((event) =>
        self.handleLayerInspectorTextConnectorAction(event),
      );
      setLayerInspectorLayerDoubleClickHandler((selection) =>
        self.handleLayerInspectorLayerDoubleClick(selection),
      );
      self.layerInspectorUnsubscribe = subscribeToLayerInspector(
        self.receiveLayerInspectorState,
        "PlayerStore.updateLayerInspectorState"
      );
    },
    beforeDestroy() {
      setLayerInspectorTransformCommitHandler(undefined);
      setLayerInspectorTextControlReportHandler(undefined);
      setLayerInspectorTextEditCommitHandler(undefined);
      setLayerInspectorTextConnectorActionHandler(undefined);
      setLayerInspectorLayerDoubleClickHandler(undefined);
      self.playerAutorunDisposer?.();
      self.playerAutorunDisposer = undefined;
      self.layerInspectorUnsubscribe?.();
      self.layerInspectorUnsubscribe = undefined;
      self.cancelPendingLayerInspectorHoverState();
    },
  }));

export type IPlayerModel = Instance<typeof PlayerModel>;

// export const playerStore = PlayerModel.create({});
